import {
  type AfterContentInit,
  Component,
  HostListener,
  type OnDestroy,
  type OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { type MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import {
  faBars,
  faCaretRight,
  faChevronDown,
  faChevronRight,
  faUserAstronaut,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { NavigationItem } from '../../Models/NavigationItem';
import { OdinNavigationService } from '../../Services/odin-navigation-service';
 
import { environment as env } from 'apps/odin-ui/src/environments/environment';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CognitoService, type UserInformation } from '@odin/odin-authentication';
import { BrandingHelper } from '../../Services/branding-helper';
import {SettingsService} from "@odin/odin-settings";
import { NotificationInitializerService } from '@odin/odin-core';

@Component({
  standalone: false,
  selector: 'odin-page-container',
  templateUrl: './page-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./page-container.component.scss'],
})
export class PageContainerComponent implements AfterContentInit {
  @ViewChild('sidenav') sidenav!: MatSidenav;
  public bhelper = BrandingHelper;

  public authenticatedView = false;
  public ddOpen = faChevronDown;
  public ddClosed = faChevronRight;
  public ddOption = faCaretRight;
  public hasUnreadNotifications = false;

  private destroy$ = new Subject<void>();

  public get userIcon(): IconDefinition {
    return faUserAstronaut;
  }
  public get menuIcon(): IconDefinition {
    return faBars;
  }
  public mobileSidebarMode: MatDrawerMode = 'over';
  public desktopSidebarMode: MatDrawerMode = 'side';
  public burgerMenuOpen = false;
  public sidebarMode: MatDrawerMode = this.desktopSidebarMode;
  public versionNumber: string =
    env.releaseVersion == '|RELEASE-VERSION|'
      ? 'Development'
      : env.releaseVersion;
  public user_information$!: Observable<UserInformation>;

  public loggingOut = false;
  public lightMode: boolean = localStorage.getItem('themeName') !== 'dark'; // check the inverse because light is default

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    setTimeout(() => {
      const target = event.target as Window;
      this.setSidebarProperties(target.innerWidth);
    }, 0);
  }

  /**
   * The shell is a property of the route, not of the token. Every route that
   * is not public is behind AuthGuardService, which already decided whether the
   * user may be here; re-checking the token in the shell (device clock vs. exp)
   * is what hid the sidenav and header on skewed clocks. Public routes opt out
   * with `data: { shell: false }`, inherited from any ancestor route.
   */
  private shouldShowShell(): boolean {
    for (let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root; route; route = route.firstChild) {
      if (route.data['shell'] === false) return false;
    }
    return true;
  }

  private setSidebarProperties(width: number) {
    if (width > 850) {
      this.sidebarMode = this.desktopSidebarMode;
      this.sidenav?.open();
      this.burgerMenuOpen = true;
    } else {
      this.sidenav?.close();
      this.sidebarMode = this.mobileSidebarMode;
    }
  }

  constructor(
    private settingService: SettingsService,
    public navigationService: OdinNavigationService,
    private router: Router,
    private cognitoService: CognitoService,
    _notificationInitializer: NotificationInitializerService,
  ) {
    this.cognitoService.loginChanged.subscribe((loggedIn: boolean) => {
      document.title = loggedIn ? 'Monek Portal' : 'Monek Portal Login';
      if (!loggedIn && this.authenticatedView) {
        // Blank the page while logout() redirects to the hosted sign-out.
        this.loggingOut = true;
      }
      this.authenticatedView = loggedIn && this.shouldShowShell();
      if (!loggedIn && this.cognitoService.userLoggedIn())
        this.router.navigate(['/']);
    });

    // Before the first navigation completes there is no route to read, so no shell.
    this.authenticatedView = this.router.navigated && this.shouldShowShell();

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        // A completed navigation means we are not mid-logout (logout() leaves
        // the SPA via window.location rather than the router).
        this.loggingOut = false;
        this.authenticatedView = this.shouldShowShell();

        // Skip token refresh on the auth-callback route to avoid racing the
        // code-exchange flow with a stale/expired refresh token.
        if (e.urlAfterRedirects.includes('/auth-callback')) return;
        this.cognitoService.checkRefresh();
      }
    });
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      this.setSidebarProperties(window.innerWidth);
    }, 0);
  }

  public ToggleLightDark(): void {
    const currentTheme = localStorage.getItem('themeName');
    if (currentTheme === null || currentTheme === 'light') {
      this.lightMode = false;
      this.settingService.SetSetting('themeName', 'dark');
    } else if (currentTheme === 'dark') {
      this.lightMode = true;
      localStorage.setItem('themeName', 'light');
      this.settingService.SetSetting('themeName', 'light');
    }
  }

  public navigate(navigationItem: NavigationItem): void {
    if (
      navigationItem.advanced !== undefined &&
      navigationItem.advanced.subRoutes !== undefined
    ) {
      navigationItem.advanced.opened = !navigationItem.advanced.opened;
      return;
    }

    if (
      navigationItem.advanced != null &&
      navigationItem.advanced.action != null
    )
      navigationItem.advanced.action();

    if (navigationItem.route != '')
      this.router.navigate([navigationItem.route]).then(() => {
        if (screen.width < 850) {
          this.sidenav.close();
        }
      });
  }
}
